import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const buyerBenefits = [
  { icon: 'ri-search-line', title: 'Browse by Category', desc: 'Search seafood, meat, poultry, vegetables and ready-to-eat across standardised spec sheets — compare like-for-like across suppliers.' },
  { icon: 'ri-contract-line', title: 'Forward Contracts', desc: 'Lock in pricing and volume with forward contracts up to 90 days. Budget with certainty, not spot-market volatility.' },
  { icon: 'ri-shopping-cart-2-line', title: 'Bulk Order Tools', desc: 'Build multi-SKU orders, set recurring schedules, and manage delivery routing to multiple locations from one dashboard.' },
  { icon: 'ri-bank-line', title: 'HORECA Credit Terms', desc: 'Qualified hotels, restaurants and caterers get net-30 and net-60 credit terms — trade at scale without working-capital strain.' },
];

const supplierBenefits = [
  { icon: 'ri-upload-cloud-line', title: 'List Harvest Volume', desc: 'Upload processed inventory from your warehouse or processing facility — get matched to verified institutional buyers instantly.' },
  { icon: 'ri-user-star-line', title: 'Verified Buyers Only', desc: 'Every buyer on the platform is verified — KYC-checked, trade-reference validated. No phantom orders, no payment risk.' },
  { icon: 'ri-git-branch-line', title: 'Automatic Storage Routing', desc: 'Your inventory gets routed to available capacity in the Storage network automatically — no need to arrange warehousing separately.' },
  { icon: 'ri-bar-chart-grouped-line', title: 'Demand Forecasting', desc: 'See aggregated demand signals by category, region and season. Plan production and pricing with data, not guesswork.' },
];

export default function BuyerSupplierSection() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section className="py-20 md:py-28 warm-sand-bg">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        {/* For Buyers */}
        <div className="mb-16 md:mb-24">
          <div className="text-center mb-10 md:mb-14">
            <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mx-auto mb-4">
              <i className="ri-shopping-bag-3-line text-2xl"></i>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-700 mb-3">
              For Buyers
            </h2>
            <p className="text-foreground-500 max-w-2xl mx-auto">
              Hotels, restaurants, caterers, retailers and institutional kitchens — source frozen food at trade scale with published specs and transparent pricing.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {buyerBenefits.map((item) => (
              <div key={item.title} className="group bg-card-50 rounded-xl p-5 md:p-6 border border-card-200 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer card-animate">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-600 mb-4 animate-icon-float icon-hover-pop">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <h4 className="font-heading text-base font-bold text-primary-700 mb-2">{item.title}</h4>
                <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-sm font-semibold text-accent-600 hover:text-accent-500 transition-colors cursor-pointer"
            >
              Apply as a Buyer
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-16 md:mb-24">
          <div className="flex-1 h-px bg-background-200/70"></div>
          <span className="text-xs font-semibold text-foreground-300 uppercase tracking-widest">Two-Sided Marketplace</span>
          <div className="flex-1 h-px bg-background-200/70"></div>
        </div>

        {/* For Suppliers */}
        <div>
          <div className="text-center mb-10 md:mb-14">
            <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 mx-auto mb-4">
              <i className="ri-store-3-line text-2xl"></i>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-700 mb-3">
              For Suppliers
            </h2>
            <p className="text-foreground-500 max-w-2xl mx-auto">
              Seafood processors, meat packers, vegetable freezing plants and co-packing facilities — reach verified institutional buyers and get routed to available warehouse capacity automatically.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {supplierBenefits.map((item) => (
              <div key={item.title} className="group bg-card-50 rounded-xl p-5 md:p-6 border border-card-200 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer card-animate">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-600 mb-4 animate-icon-float icon-hover-pop">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <h4 className="font-heading text-base font-bold text-primary-700 mb-2">{item.title}</h4>
                <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-sm font-semibold text-secondary-600 hover:text-secondary-500 transition-colors cursor-pointer"
            >
              Apply as a Supplier
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}